import {Component, OnInit, inject, signal} from '@angular/core';
import {AlertLevel} from '../../../enums/alert-level';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {describeAdministrationError} from '../../../_shared/errors/administration-errors';
import {PlatformSettings, PlatformSettingsHttpService} from '../_services/platform-settings-http.service';

/**
 * **Plattform** — the platform-wide settings that are not an integration credential (#608).
 *
 * ### Why this screen exists
 *
 * Until #608 these values lived in `/owner/environment`, a raw key→value table: an operator had to know that
 * the public base URL is spelled `domain` and that the register app's version is two separate rows called
 * `app_version` and `app_build`, and nothing on that screen said what any of them did or who read them. Each
 * one now has a typed field with its reader named underneath it, and the editor is gone — a key nobody can
 * name is a key nobody can maintain.
 *
 * ### Two cards, two saves, and each sends ONLY its own fields
 *
 * The address is read by exactly one consumer (the Telegram password bot, which appends
 * `/static/privacy.html`). The version and build have exactly one reader too, and it is **not** the register:
 * they ride in on the SignalR group model and the shard **device list**
 * (`_modules/signalRTracker/devices/devices.component.html`) compares each device's *reported* version and
 * build against them, marking a mismatch red under „App-Version". They are a comparison target, not a push —
 * nothing here updates or instructs a till, and the copy on the card says so, because "wird an die Kasse
 * weitergegeben" would describe a rollout mechanism the product does not have. Nothing links the two cards, so
 * each saves on its own and reports its own outcome.
 *
 * The write has **three** states per field — `null`/omitted leaves it alone, `''` deletes its row, anything
 * else stores it — which is what makes the split safe and echoing unsafe. A card that sent the other card's
 * last-loaded values would overwrite whatever a second operator changed in between, and would *delete* them
 * outright if it ever fired before the first read landed. That second hazard is why every control is disabled
 * while {@link loading} is true: the blank state of this screen is indistinguishable, on the wire, from an
 * instruction to clear everything.
 *
 * ### The address rule is narrower than "a valid URL"
 *
 * Scheme plus host, nothing else: its one reader appends a path, so a stored path would produce
 * `…/impressum/static/privacy.html`. The server refuses anything else with `E_PLATFORM_DOMAIN_INVALID`, and
 * that refusal is mapped to a German sentence that states the shape rather than the rule's name. What it
 * *accepts* it also normalises — trailing slash stripped, host lower-cased, a default port dropped — so the
 * screen re-renders from the response and never from what was typed.
 */
@Component({
  selector: 'app-owner-platform',
  standalone: true,
  templateUrl: './platform.component.html'
})
export class PlatformComponent implements OnInit {
  private readonly http = inject(PlatformSettingsHttpService);
  private readonly alerts = inject(GlobalAlertService);

  /** The last state the server reported. `''` rather than `null` throughout — see the DTO's doc. */
  protected readonly domain = signal('');
  protected readonly appVersion = signal('');
  protected readonly appBuild = signal('');

  protected readonly loading = signal(true);
  protected readonly savingDomain = signal(false);
  protected readonly savingApp = signal(false);

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.http.get().subscribe({
      next: settings => {
        this.loading.set(false);
        this.apply(settings);
      },
      error: err => {
        this.loading.set(false);
        this.fail(err, 'Die Plattformeinstellungen konnten nicht geladen werden.');
      }
    });
  }

  protected saveDomain(domain: string): void {
    if (this.savingDomain()) return;
    this.savingDomain.set(true);

    // ONLY this card's field. `null` means "leave it alone" and `''` means "delete it", so echoing the other
    // card's last-loaded values here would overwrite whatever a second operator changed in between — and, in
    // the window before the first read lands, would delete them outright.
    this.http.set({domain: domain.trim()}).subscribe({
      next: settings => {
        this.savingDomain.set(false);
        this.apply(settings);
        this.succeed('Die öffentliche Adresse wurde gespeichert.');
      },
      error: err => {
        this.savingDomain.set(false);
        this.fail(err, 'Die öffentliche Adresse konnte nicht gespeichert werden.');
      }
    });
  }

  protected saveApp(appVersion: string, appBuild: string): void {
    if (this.savingApp()) return;
    this.savingApp.set(true);

    // Version and build together — they are one fact — and the address deliberately absent; see `saveDomain`.
    this.http.set({appVersion: appVersion.trim(), appBuild: appBuild.trim()}).subscribe({
      next: settings => {
        this.savingApp.set(false);
        this.apply(settings);
        this.succeed('Version und Build der Kassen-App wurden gespeichert.');
      },
      error: err => {
        this.savingApp.set(false);
        this.fail(err, 'Version und Build der Kassen-App konnten nicht gespeichert werden.');
      }
    });
  }

  /**
   * The value of the input that raised `event`.
   *
   * Every field here is **controlled** — `[value]` bound to a signal, `(input)` writing it back — rather than
   * read off a template reference at save time. A property binding is only written when the *evaluated* value
   * changes, so if the component kept the loaded value while the DOM held a typed one, the server's
   * normalisation (the stripped trailing slash) would evaluate to the value the signal already had, Angular
   * would write nothing, and the field would keep showing the address that was rejected or rewritten.
   */
  protected value(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /**
   * Adopts what the server stored — never what was typed.
   *
   * The server normalises (it trims, and strips a trailing slash from the address), so re-rendering from the
   * response is what shows the operator the value that is actually in force. A screen that kept the typed text
   * would disagree with the stored one after every save that changed anything.
   */
  private apply(settings: PlatformSettings): void {
    this.domain.set(settings.domain ?? '');
    this.appVersion.set(settings.appVersion ?? '');
    this.appBuild.set(settings.appBuild ?? '');
  }

  private fail(err: unknown, fallback: string): void {
    this.alerts.createAndShow('Fehler', describeAdministrationError(err, fallback), AlertLevel.error);
  }

  private succeed(message: string): void {
    this.alerts.createAndShow('Gespeichert', message, AlertLevel.success);
  }
}
